<template>
  <div :class="[$style.config, $style.wrap]">
    <header
      v-if="
        data?.fields?.header || data?.fields?.subheader || data?.fields?.copy
      "
      :class="$style.header"
    >
      <BaseHeading size="h4">
        {{ data?.fields?.header }}
      </BaseHeading>
      <BaseHeading size="h6">
        {{ data?.fields?.subheader }}
      </BaseHeading>
      <MarkedParser :class="$style.copy" :md="data?.fields?.copy" />
    </header>

    <div v-if="computedCards.length" :class="$style.container">
      <BaseCard
        v-for="c in computedCards"
        :key="c?.sys?.id"
        :class="$style.card"
      >
        <template v-if="c?.fields?.media" #media>
          <AspectRatio
            :ratio="`${c?.fields?.media?.fields?.file?.details?.image?.width}x${c?.fields?.media?.fields?.file?.details?.image?.height}`"
          >
            <BasePicture
              :src="c?.fields?.media?.fields?.file?.url + '?w=520&fm=webp'"
              alt=""
            />
          </AspectRatio>
        </template>
        <template v-if="c?.fields?.copy" #body>
          <MarkedParser :md="c?.fields?.copy" />
        </template>
        <template v-if="c?.fields?.ctas" #actions>
          <div :class="$style.ctas">
            <CallToAction
              v-for="cta in c?.fields?.ctas"
              :key="cta?.sys?.id"
              :cta="cta"
            />
          </div>
        </template>
      </BaseCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  TypeCardWithoutUnresolvableLinksResponse,
  TypeContentBlockWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import { instanceOfCard } from '~/utils/instanceOfEntry'

const { $visibility } = useNuxtApp()

const $style = useCssModule()

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const computedCards = computed(() => {
  const cards = props.data?.fields?.references?.filter?.(instanceOfCard) || []

  const filtered = cards.filter((c: TypeCardWithoutUnresolvableLinksResponse) =>
    $visibility(c.fields?.visibility),
  )

  return filtered
})
</script>

<style lang="scss" module>
.config {
  --wrap-padding--base: 40px 20px;
  --wrap-padding--md: 60px 60px;
  --wrap-padding: var(--wrap-padding--base);

  --container-max-width: 1220px;
  --card-max-width: 520px;

  --container-gap--base: 2em;
  --container-gap--sm: 40px;
  --container-gap: var(--container-gap--base);

  --container-grid-template-columns--base: 1fr;
  --container-grid-template-columns--sm: 1fr 1fr 1fr;
  --container-grid-template-columns: var(
    --container-grid-template-columns--base
  );

  @include media('sm') {
    --container-gap: var(--container-gap--sm);
    --container-grid-template-columns: var(
      --container-grid-template-columns--sm
    );
  }

  @include media('md') {
    --wrap-padding: var(--wrap-padding--md);
  }
}

.wrap {
  padding: var(--wrap-padding);
}

.header {
  text-align: center;
  margin-bottom: 20px;
}

.container {
  display: grid;
  grid-template-columns: var(--container-grid-template-columns);
  gap: var(--container-gap);
  align-items: center;

  max-width: var(--container-max-width);
  margin: 0 auto;
}

.card {
  border-bottom: 5px solid var(--purple);
  width: 100%;
  max-width: var(--card-max-width);
  margin: 0 auto;
}

.ctas {
  text-align: center;
}
</style>
